import { useEffect, useState } from 'react';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { displayPhone, mailtoHref, telHref } from '../../../lib/contactLinks';

import {
  TRACKER_CELL_INPUT_SX,
  TRACKER_GOING_TONE,
  TRACKER_SELECTED_ROW_SX,
  TRACKER_SURFACE_SX,
  TRACKER_TABLE_SX,
  GUEST_TEXT_BUTTON_SX,
} from './guestTableStyles';
import GuestTagChips from './GuestTagChips';
import {
  TRACKER_BOOKING_SOURCE_LABELS,
  TRACKER_GOING_LABELS,
  TRACKER_GOING_STATES,
  type GuestTag,
  type TrackerGoingState,
  type TrackerGuest,
  type TrackerPatch,
} from '../../../services/tracker-api-client';
import {
  formatEditedBy,
  guestBookingSource,
  guestGoingState,
  orderGuestsWithPlusOnes,
  toDateInputValue,
} from '../../../lib/trackerPresentation';

export type RowSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface RowSaveState {
  status: RowSaveStatus;
  message?: string;
}

/**
 * Sentinel for the "Clear override" row in the RSVP select. Deliberately not a
 * TrackerGoingState so it cannot be mistaken for one, and it never escapes this
 * component — onChange translates it to an explicit null, which the patch
 * contract reads as "clear" (an omitted key means "unchanged").
 */
const CLEAR_GOING_OVERRIDE = '__clear_going_override__';

/**
 * Roster fields for one guest, joined in from `group.invitees`.
 *
 * The tracker board does not carry these — segment, source and the invite token
 * live on the invitee row, which the page has already loaded. Keeping them in a
 * separate shape rather than widening TrackerGuest keeps it honest about which
 * of the two the value came from, and which write path can change it.
 */
export interface GuestRoster {
  segment: string | null;
  source: string;
  inviteUrl: string;
}

export type GuestColumnGroup = 'roster' | 'logistics';

export interface GuestTableProps {
  guests: TrackerGuest[];
  roster: Readonly<Record<string, GuestRoster>>;
  visibleGroups: readonly GuestColumnGroup[];
  stayingAtOptions: string[];
  rowState: Readonly<Record<string, RowSaveState>>;
  selectedInviteeId: string | null;
  invitingInviteeId: string | null;
  onSelect: (inviteeId: string | null) => void;
  onPatch: (inviteeId: string, patch: TrackerPatch) => void;
  onRetry: (inviteeId: string) => void;
  onSendInvite: (inviteeId: string) => void;
  /**
   * Only ever fired against an API with no tags — the Segment cell is not drawn
   * otherwise. See {@link SEGMENT_OPTIONS}.
   */
  onSegmentChange: (inviteeId: string, segment: string | null) => void;
  segmentSaving: boolean;
  /**
   * The tag vocabulary, or null when this API has no tags. Null removes the
   * selection column and the Tags column outright rather than drawing controls
   * that write nowhere.
   */
  vocabulary: GuestTag[] | null;
  selected: ReadonlySet<string>;
  /** Header checkbox: on, off, or partly — over the rows the filter left. */
  allVisible: 'none' | 'some' | 'all';
  /** `shiftKey` extends the range from the last row ticked. */
  onToggleSelect: (inviteeId: string, shiftKey: boolean) => void;
  onToggleAllVisible: () => void;
}

/**
 * The two cohorts the roster could name before tags existed, and the reason
 * this list is still here.
 *
 * Segment and a tag say the same thing about a guest — "Dario is core" — and
 * broadcasts now resolve that cohort through tag membership rather than through
 * this column. Two controls editing one fact drift: taking the `core` tag off
 * somebody in the palette leaves this cell still reading Core, and the host is
 * then looking at a label that targets nobody. So on an API that has tags, this
 * editor is not drawn at all and the palette is the only way to say it.
 *
 * It survives for the API that has no tag routes yet, where it is the ONLY way
 * to say it and the server still filters broadcasts on the column. Studio and
 * the concierge API deploy separately; removing it outright would stand a host
 * in front of a roster with no way to name a cohort at all.
 */
export const SEGMENT_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'core', label: 'Core' },
  { value: 'travel_party', label: 'Travel party' },
];

/** Empty string clears the stored value; the API distinguishes null from absent. */
function orNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Blank clears the party size. A non-numeric value is dropped rather than sent:
 * `JSON.stringify(NaN)` is `null`, which the API reads as "clear this field" —
 * a typo would silently wipe a number the host had already entered.
 */
function orNullNumber(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function SaveIndicator({ state, onRetry }: { state?: RowSaveState; onRetry: () => void }) {
  if (!state || state.status === 'idle') return null;
  if (state.status === 'saving') {
    return <CircularProgress size={14} aria-label="Saving" sx={{ color: 'var(--tsb-fg-muted)' }} />;
  }
  if (state.status === 'saved') {
    return (
      <CheckRoundedIcon
        role="img"
        aria-label="Saved"
        sx={{ fontSize: 17, color: 'var(--tsb-brand-ink)' }}
      />
    );
  }
  return (
    <Tooltip title={state.message || "That edit didn't save."}>
      <Button
        size="small"
        variant="text"
        onClick={(event) => {
          event.stopPropagation();
          onRetry();
        }}
        startIcon={<ErrorOutlineRoundedIcon sx={{ fontSize: 16 }} />}
        sx={{
          borderRadius: 'var(--tsb-radius-sm)',
          color: 'var(--tsb-danger-ink)',
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'none',
          minWidth: 0,
          px: 0.75,
          '&:hover': { bgcolor: 'var(--tsb-danger-bg)' },
          '&.Mui-focusVisible': { boxShadow: 'var(--tsb-focus-ring)' },
        }}
      >
        Retry
      </Button>
    </Tooltip>
  );
}

/**
 * A cell that commits on blur. Held locally while focused so a background
 * refetch (or another host's edit) cannot overwrite half-typed text.
 */
function CommitOnBlurCell({
  value,
  ariaLabel,
  type = 'text',
  width,
  onCommit,
}: {
  value: string;
  ariaLabel: string;
  type?: 'text' | 'date' | 'number';
  width: number;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  return (
    <TextField
      size="small"
      type={type}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        if (draft !== value) onCommit(draft);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
        if (event.key === 'Escape') {
          setDraft(value);
          (event.target as HTMLInputElement).blur();
        }
      }}
      onClick={(event) => event.stopPropagation()}
      inputProps={{ 'aria-label': ariaLabel, ...(type === 'number' ? { min: 1, max: 20 } : {}) }}
      sx={{ ...TRACKER_CELL_INPUT_SX, width }}
    />
  );
}

/**
 * The invite link is a per-guest bearer credential, so it is offered as a copy
 * action rather than printed in full: 137 of them rendered as text turns one
 * shoulder-surf or screenshot into everyone's access, and the host only ever
 * needs to paste one.
 */
function InviteLinkCell({ url, guestName }: { url: string; guestName: string }) {
  const [copied, setCopied] = useState(false);

  if (!url) {
    return <Typography sx={{ color: 'var(--tsb-fg-faint)', fontSize: 12 }}>—</Typography>;
  }

  return (
    <Button
      size="small"
      variant="text"
      aria-label={`Copy invite link for ${guestName}`}
      onClick={(event) => {
        event.stopPropagation();
        void navigator.clipboard
          .writeText(url)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2_000);
          })
          // Clipboard access is refused in some browser configurations; the
          // link is still reachable from the record editor below.
          .catch(() => setCopied(false));
      }}
      sx={{ fontSize: 12, minWidth: 0, whiteSpace: 'nowrap' }}
    >
      {copied ? 'Copied' : 'Copy link'}
    </Button>
  );
}

export function GuestTable({
  guests,
  roster,
  visibleGroups,
  stayingAtOptions,
  rowState,
  selectedInviteeId,
  invitingInviteeId,
  onSelect,
  onPatch,
  onRetry,
  onSendInvite,
  onSegmentChange,
  segmentSaving,
  vocabulary,
  selected,
  allVisible,
  onToggleSelect,
  onToggleAllVisible,
}: GuestTableProps) {
  const rows = orderGuestsWithPlusOnes(guests);
  const showRoster = visibleGroups.includes('roster');
  const showLogistics = visibleGroups.includes('logistics');

  return (
    <Paper elevation={0} sx={{ ...TRACKER_SURFACE_SX, overflow: 'hidden' }}>
      {/* Studio's shell caps content at 1200px and never scrolls sideways, so
          the table has to carry its own horizontal scroll. */}
      <Box sx={{ overflowX: 'auto', maxHeight: { md: '68vh' }, overflowY: 'auto' }}>
        <Box component="table" sx={TRACKER_TABLE_SX}>
          <caption
            style={{
              captionSide: 'top',
              padding: '12px 12px 10px',
              textAlign: 'left',
              fontSize: 12,
              lineHeight: 1.5,
              color: 'var(--tsb-fg-muted)',
            }}
          >
            {`${guests.length} ${guests.length === 1 ? 'guest' : 'guests'} — click a cell to edit, click a name to open the full record below. Going, Staying and Dinners fill themselves from RSVPs and claims.`}
            {vocabulary
              ? ' Tick rows to tag several at once, or narrow the list above and tag the whole thing.'
                + ' Core and Travel party are tags now, not a Segment column.'
              : ''}
          </caption>
          <Box component="thead">
            <Box component="tr">
              {vocabulary ? (
                <Box component="th" scope="col" sx={{ width: 44 }}>
                  <Checkbox
                    size="small"
                    // Over the rows the FILTER left, never the whole roster: a
                    // select-all that also picks up 125 guests scrolled out of
                    // existence by a search box is a bulk write waiting to go
                    // to the wrong people.
                    checked={allVisible === 'all'}
                    indeterminate={allVisible === 'some'}
                    onChange={onToggleAllVisible}
                    inputProps={{ 'aria-label': 'Select every guest this filter shows' }}
                  />
                </Box>
              ) : null}
              <Box component="th" scope="col">
                Guest
              </Box>
              <Box component="th" scope="col">
                Going?
              </Box>
              {vocabulary ? (
                <Box component="th" scope="col">
                  Tags
                </Box>
              ) : null}
              {showRoster ? (
                <>
                  <Box component="th" scope="col">
                    Email
                  </Box>
                  <Box component="th" scope="col">
                    Phone
                  </Box>
                  {/* Only where there are no tags to say it with — see SEGMENT_OPTIONS. */}
                  {vocabulary === null ? (
                    <Box component="th" scope="col">
                      Segment
                    </Box>
                  ) : null}
                  <Box component="th" scope="col">
                    Source
                  </Box>
                  <Box component="th" scope="col">
                    Invite link
                  </Box>
                </>
              ) : null}
              {showLogistics ? (
                <>
                  <Box component="th" scope="col">
                    Arrive
                  </Box>
                  <Box component="th" scope="col">
                    Depart
                  </Box>
                  <Box component="th" scope="col">
                    Staying at
                  </Box>
                  <Box component="th" scope="col">
                    Party
                  </Box>
                  <Box component="th" scope="col">
                    Dinners
                  </Box>
                </>
              ) : null}
              <Box component="th" scope="col">
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                    clip: 'rect(0 0 0 0)',
                  }}
                >
                  Actions
                </Box>
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map(({ guest, depth, plusOneOf }) => {
              const going = guestGoingState(guest);
              const tone = TRACKER_GOING_TONE[going];
              const bookedVia = guestBookingSource(guest);
              const edited = formatEditedBy(guest.updatedBy, guest.updatedAt);
              // `open` is the record editor below; `ticked` is the bulk
              // selection. Two different things, and conflating them is how a
              // host opens a record and finds eight people tagged.
              const open = guest.inviteeId === selectedInviteeId;
              const ticked = selected.has(guest.inviteeId);
              const hasEmail = Boolean(guest.email?.trim());
              const rosterRow = roster[guest.inviteeId];

              return (
                <Box
                  component="tr"
                  key={guest.inviteeId}
                  onClick={() => onSelect(open ? null : guest.inviteeId)}
                  sx={{ cursor: 'pointer', ...(open ? TRACKER_SELECTED_ROW_SX : {}) }}
                >
                  {vocabulary ? (
                    <Box component="td" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={ticked}
                        // Shift extends from the last row ticked. A range always
                        // ADDS — shift-clicking to extend and having the middle
                        // clear is the classic surprise, and here it costs a tag
                        // on the wrong people.
                        onClick={(event) =>
                          onToggleSelect(guest.inviteeId, (event as React.MouseEvent).shiftKey)
                        }
                        inputProps={{ 'aria-label': `Select ${guest.fullName}` }}
                      />
                    </Box>
                  ) : null}
                  <Box component="th" scope="row" sx={{ fontWeight: 400 }}>
                    <Box sx={{ pl: depth ? 3 : 0, minWidth: 0 }}>
                      {/*
                        The row click is a mouse affordance; the name is the
                        real control, so the record editor is reachable by
                        keyboard and announced as expandable.
                      */}
                      <Typography
                        component="button"
                        type="button"
                        aria-expanded={open}
                        onClick={(event) => {
                          event.stopPropagation();
                          onSelect(open ? null : guest.inviteeId);
                        }}
                        sx={{
                          p: 0,
                          border: 'none',
                          bgcolor: 'transparent',
                          textAlign: 'left',
                          cursor: 'pointer',
                          color: 'var(--tsb-fg)',
                          fontFamily: 'inherit',
                          fontSize: 13.5,
                          fontWeight: depth ? 500 : 600,
                          lineHeight: 1.35,
                          overflowWrap: 'anywhere',
                          '&:hover': { color: 'var(--tsb-brand-ink)', textDecoration: 'underline' },
                          '&:focus-visible': {
                            outline: '2px solid var(--tsb-brand-hover)',
                            outlineOffset: 2,
                            borderRadius: 'var(--tsb-radius-xs)',
                          },
                        }}
                      >
                        {guest.fullName}
                      </Typography>
                      <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ columnGap: 1 }}>
                        {plusOneOf ? (
                          <Typography
                            sx={{ color: 'var(--tsb-brand-ink)', fontSize: 11.5, fontWeight: 600 }}
                          >
                            {`+1 of ${plusOneOf}`}
                          </Typography>
                        ) : null}
                        {/*
                          Contact details a host can act on rather than retype.
                          At 19:40 in a hotel lobby, working out who is not at
                          the bus, retyping is where digits get dropped — and
                          the person you cannot reach is the one you are looking
                          for.

                          `stopPropagation` because the row itself opens the
                          record: without it, tapping the number both dials and
                          opens the editor behind the call.
                        */}
                        {(() => {
                          // Computed once. Each helper was called twice per
                          // guest — 548 regex passes per render on a 137-row
                          // roster, repeated on every keystroke in the filter.
                          // `hasEmail` is the file's own trimmed predicate; the
                          // raw truthiness check rendered a blank muted line
                          // for an address of "   ".
                          const mailto = mailtoHref(guest.email);
                          const tel = telHref(guest.phone);
                          const phoneText = displayPhone(guest.phone);
                          return (
                            <>
                              {hasEmail ? (
                                mailto ? (
                                  <Link
                                    href={mailto}
                                    onClick={(event) => event.stopPropagation()}
                                    sx={{ color: 'var(--tsb-fg-muted)', fontSize: 11.5 }}
                                  >
                                    {guest.email}
                                  </Link>
                                ) : (
                                  // Shown, not linked. A malformed address is
                                  // something the host needs to SEE to fix.
                                  <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 11.5 }}>
                                    {guest.email}
                                  </Typography>
                                )
                              ) : null}
                              {phoneText ? (
                                tel ? (
                                  <Link
                                    href={tel}
                                    onClick={(event) => event.stopPropagation()}
                                    sx={{ color: 'var(--tsb-fg-muted)', fontSize: 11.5 }}
                                  >
                                    {phoneText}
                                  </Link>
                                ) : (
                                  <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 11.5 }}>
                                    {phoneText}
                                  </Typography>
                                )
                              ) : null}
                            </>
                          );
                        })()}
                      </Stack>
                      {edited ? (
                        // `fg-faint` is disabled-only in the token map; this is
                        // real text a host reads, so it stays on `fg-muted`.
                        <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 11.5 }}>
                          {edited}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>

                  <Box component="td">
                    <TextField
                      select
                      size="small"
                      value={going}
                      onChange={(event) => {
                        /*
                          CLEAR_GOING_OVERRIDE sends an explicit null, which the
                          patch contract reads as "clear" (omitted means
                          unchanged). Without it this dropdown was a one-way
                          door: every option wrote a host override, so a misclick
                          anywhere in 137 rows permanently detached that guest
                          from their imported RSVP with no route back in the UI.
                        */
                        const value = event.target.value;
                        onPatch(guest.inviteeId, {
                          going:
                            value === CLEAR_GOING_OVERRIDE ? null : (value as TrackerGoingState),
                        });
                      }}
                      onClick={(event) => event.stopPropagation()}
                      SelectProps={{
                        SelectDisplayProps: { 'aria-label': `RSVP for ${guest.fullName}` },
                      }}
                      sx={{
                        ...TRACKER_CELL_INPUT_SX,
                        width: 128,
                        '& .MuiSelect-select': {
                          bgcolor: tone.bg,
                          color: tone.fg,
                          borderRadius: 'var(--tsb-radius-pill)',
                          fontSize: 12,
                          fontWeight: 600,
                        },
                      }}
                    >
                      {TRACKER_GOING_STATES.map((state) => (
                        <MenuItem key={state} value={state}>
                          {TRACKER_GOING_LABELS[state]}
                        </MenuItem>
                      ))}
                      <MenuItem value={CLEAR_GOING_OVERRIDE}>
                        <Box component="span" sx={{ color: 'var(--tsb-fg-muted)' }}>
                          Clear override
                        </Box>
                      </MenuItem>
                    </TextField>
                  </Box>

                  {vocabulary ? (
                    <Box component="td">
                      <GuestTagChips
                        tagIds={guest.tagIds}
                        tagNotes={guest.tagNotes}
                        vocabulary={vocabulary}
                      />
                    </Box>
                  ) : null}

                  {showRoster ? (
                    <>
                      <Box component="td">
                        <CommitOnBlurCell
                          width={230}
                          ariaLabel={`Email for ${guest.fullName}`}
                          value={guest.email ?? ''}
                          onCommit={(next) =>
                            onPatch(guest.inviteeId, { contactEmail: orNull(next) })
                          }
                        />
                      </Box>
                      <Box component="td">
                        <CommitOnBlurCell
                          width={170}
                          ariaLabel={`Phone for ${guest.fullName}`}
                          value={guest.phone ?? ''}
                          onCommit={(next) =>
                            onPatch(guest.inviteeId, { contactPhone: orNull(next) })
                          }
                        />
                      </Box>
                      {/*
                        Segment is the one editable cell that is NOT a tracker
                        field — it lives on the invitee row and goes out through
                        the import upsert.

                        Drawn only against an API with no tags, where it is the
                        one way to name a cohort and the one the broadcast
                        filter reads. Where there are tags, the palette owns
                        that fact and this cell would be a second control
                        editing it — see SEGMENT_OPTIONS for what that costs.

                        This cell used to be disabled for a guest with no email
                        and no export id, because the upsert had nothing to
                        match them on and a segment write would have added a
                        second row. That is no longer how the write goes out:
                        GroupDetail's `inviteeToInput` sends the invitee's own
                        id, which resolves every guest including the Partiful
                        ones the guard was blocking. Leaving the guard in place
                        disabled the control for exactly the roster it was
                        meant to protect — and the CSV parser dropping its
                        synthetic export ids widened that to every guest
                        imported without an email address.
                      */}
                      {vocabulary === null ? (
                        <Box component="td">
                          <TextField
                            select
                            size="small"
                            value={rosterRow?.segment ?? ''}
                            disabled={segmentSaving}
                            onChange={(event) =>
                              onSegmentChange(guest.inviteeId, event.target.value || null)
                            }
                            onClick={(event) => event.stopPropagation()}
                            SelectProps={{
                              SelectDisplayProps: { 'aria-label': `Segment for ${guest.fullName}` },
                            }}
                            sx={{ ...TRACKER_CELL_INPUT_SX, width: 150 }}
                          >
                            {SEGMENT_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      ) : null}
                      <Box component="td">
                        <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 12 }}>
                          {rosterRow?.source ?? '—'}
                        </Typography>
                      </Box>
                      <Box component="td">
                        <InviteLinkCell
                          url={rosterRow?.inviteUrl ?? ''}
                          guestName={guest.fullName}
                        />
                      </Box>
                    </>
                  ) : null}

                  {showLogistics ? (
                  <>
                  <Box component="td">
                    <CommitOnBlurCell
                      type="date"
                      width={150}
                      ariaLabel={`Arrival date for ${guest.fullName}`}
                      value={toDateInputValue(guest.arrivalDate)}
                      onCommit={(next) => onPatch(guest.inviteeId, { arrivalDate: orNull(next) })}
                    />
                  </Box>
                  <Box component="td">
                    <CommitOnBlurCell
                      type="date"
                      width={150}
                      ariaLabel={`Departure date for ${guest.fullName}`}
                      value={toDateInputValue(guest.departureDate)}
                      onCommit={(next) => onPatch(guest.inviteeId, { departureDate: orNull(next) })}
                    />
                  </Box>

                  <Box component="td">
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={stayingAtOptions}
                      value={guest.stayingAt ?? ''}
                      onChange={(_event, next) =>
                        onPatch(guest.inviteeId, {
                          stayingAt: orNull(typeof next === 'string' ? next : ''),
                        })
                      }
                      onBlur={(event) => {
                        const next = (event.target as HTMLInputElement).value ?? '';
                        if (next.trim() !== (guest.stayingAt ?? '').trim()) {
                          onPatch(guest.inviteeId, { stayingAt: orNull(next) });
                        }
                      }}
                      sx={{ width: 210 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Add a hotel"
                          onClick={(event) => event.stopPropagation()}
                          inputProps={{
                            ...params.inputProps,
                            'aria-label': `Staying at, for ${guest.fullName}`,
                          }}
                          sx={TRACKER_CELL_INPUT_SX}
                        />
                      )}
                    />
                    {bookedVia ? (
                      // Booking source is automatic, so it reads as a note under
                      // the hotel rather than a column of its own. Tripsby
                      // bookings carry the brand ink; outside ones stay muted.
                      <Typography
                        sx={{
                          mt: 0.25,
                          pl: 1,
                          color:
                            bookedVia === 'tripsby'
                              ? 'var(--tsb-brand-ink)'
                              : 'var(--tsb-fg-muted)',
                          fontSize: 11.5,
                          fontWeight: 600,
                        }}
                      >
                        {bookedVia === 'tripsby'
                          ? `Booked via ${TRACKER_BOOKING_SOURCE_LABELS.tripsby}`
                          : 'Booked by guest'}
                      </Typography>
                    ) : null}
                  </Box>

                  <Box component="td">
                    <CommitOnBlurCell
                      type="number"
                      width={78}
                      ariaLabel={`Party size for ${guest.fullName}`}
                      value={guest.partySize == null ? '' : String(guest.partySize)}
                      onCommit={(next) => {
                        const partySize = orNullNumber(next);
                        if (partySize !== undefined) onPatch(guest.inviteeId, { partySize });
                      }}
                    />
                  </Box>

                  <Box component="td">
                    <Typography
                      sx={{
                        // An em dash is a placeholder, which is the one role
                        // `fg-faint` is allowed to carry.
                        color: guest.dinners > 0 ? 'var(--tsb-fg-body)' : 'var(--tsb-fg-faint)',
                        fontSize: 13,
                      }}
                    >
                      {guest.dinners > 0 ? guest.dinners : '—'}
                    </Typography>
                  </Box>
                  </>
                  ) : null}

                  <Box component="td">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      justifyContent="flex-end"
                    >
                      <SaveIndicator
                        state={rowState[guest.inviteeId]}
                        onRetry={() => onRetry(guest.inviteeId)}
                      />
                      <Tooltip
                        title={
                          hasEmail
                            ? 'Email this guest their invite link'
                            : 'Add an email address in the record below first'
                        }
                      >
                        {/* A disabled button drops pointer events, so the tooltip needs a live wrapper. */}
                        <Box component="span">
                          <Button
                            size="small"
                            variant="text"
                            disabled={!hasEmail || invitingInviteeId === guest.inviteeId}
                            onClick={(event) => {
                              event.stopPropagation();
                              onSendInvite(guest.inviteeId);
                            }}
                            startIcon={<MailOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                            aria-label={`Send invite to ${guest.fullName}`}
                            sx={{
                              ...GUEST_TEXT_BUTTON_SX,
                              fontSize: 12.5,
                              whiteSpace: 'nowrap',
                              '&.Mui-disabled': { color: 'var(--tsb-fg-faint)' },
                            }}
                          >
                            {invitingInviteeId === guest.inviteeId ? 'Sending…' : 'Send invite'}
                          </Button>
                        </Box>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

export default GuestTable;
